import { WarningIcon } from '@/assets/icons/warning-icon';
import { useToast } from '@/components/ui/use-toast';
import { BaseConfirmationDialog } from '@/features/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import type { ModalInnerProps } from '@/features/modal';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CrmCommentDTO } from '@/server/dto/crm-comment-dto';
import { api } from '@/utils/api';
import { useState } from 'react';

export type DeleteCommentDialogProps = {
    comment: CrmCommentDTO;
    onDeleted?: () => void;
} & ModalInnerProps;

export const DeleteCommentDialog = DialogStateManager.create<DeleteCommentDialogProps>(
    ({ comment, onDeleted }: DeleteCommentDialogProps) => {
        const { t } = useLocale('tasks');
        const { toast } = useToast();
        const [isDeletingComment, setIsDeletingComment] = useState(false);
        const modal = useDialog();
        const apiUtils = api.useUtils();

        const { taskId, noteId, commentId } = comment;

        const deleteMutation = api.platoUser.command.comments.delete.useMutation({
            onSuccess() {
                // Optimistically update the cache by removing the deleted comment
                apiUtils.platoUser.query.comments.getManyComments.setData(
                    taskId ? { taskId: taskId } : { noteId: noteId as string },
                    (oldData) => {
                        if (!oldData) return [];
                        return oldData.filter((comment) => comment.commentId !== commentId);
                    },
                );

                toast({
                    icon: 'success',
                    title: t('comments__toast__deleted_success_title'),
                });

                onDeleted?.();
                modal.remove();
            },
            onError: (error) => {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
                setIsDeletingComment(false);
            },
        });

        const handleSubmit = async () => {
            setIsDeletingComment(true);
            deleteMutation.mutate({ commentId });
        };

        const handleCloseConfirmation = () => {
            modal.remove();
        };

        return (
            <BaseConfirmationDialog
                focus="destroy"
                title={t('comments__delete_confirmation__title')}
                description={t('comments__delete_confirmation__body')}
                icon={WarningIcon}
            >
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={handleCloseConfirmation}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleSubmit} isLoading={isDeletingComment}>
                        {t('form__delete', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
